import api from "@/config/axiosConfig";
import { auth, githubProvider, googleProvider, microsoftProvider } from "@/firebase";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User as FirebaseUser,
} from "firebase/auth";
import { updateUserContext } from "./store";

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  providerId: string;
}

export const checkIsAuthenticated = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return false;
    }

    // Get a fresh Firebase ID token
    const token = await currentUser.getIdToken(true);
    
    // Store the Firebase token for API calls
    localStorage.setItem("firebaseToken", token);
    
    return true;
  } catch (error) {
    console.error("Authentication check failed:", error);
    return false;
  }
};

export const logout = async () => {
  try {
    // Sign out from Firebase
    await signOut(auth);

    // Clear local storage
    localStorage.removeItem("firebaseToken");
    localStorage.removeItem("customToken");
    localStorage.removeItem("firebaseUser");
    localStorage.removeItem("userContext");

    // Redirect to login
    window.location.href = "/login";
  } catch (error) {
    console.error("Logout error:", error);
    // Force redirect even if Firebase logout fails
    window.location.href = "/login";
  }
};

// Firebase Authentication Functions
export const signInWithGoogle = async (): Promise<AuthUser | null> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    const authUser: AuthUser = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      providerId: "google.com",
    };

    // Send to backend for verification/registration
    await authenticateWithBackend(authUser, await user.getIdToken());

    return authUser;
  } catch (error) {
    console.error("Google sign-in error:", error);
    throw error;
  }
};

export const signInWithGithub = async (): Promise<AuthUser | null> => {
  try {
    const result = await signInWithPopup(auth, githubProvider);
    const user = result.user;

    // For GitHub, we might need to get additional user info
    let email = user.email;
    let displayName = user.displayName;

    // If email is not available, try to get it from additional user info
    if (!email) {
      console.log(
        "GitHub user email not available, trying to get additional info..."
      );
      // For GitHub users without email, we'll use a fallback
      // This should be handled by the backend validation
      email = `${user.uid}@github.user`;
      console.warn("Using fallback email for GitHub user:", email);
    }

    // Try to extract GitHub username from displayName or photoURL
    let githubUsername = null;
    if (displayName) {
      // Sometimes displayName contains the GitHub username
      githubUsername = displayName.replace(/\s+/g, "");
    } else if (user.photoURL && user.photoURL.includes("github.com")) {
      // Extract from GitHub avatar URL
      const match = user.photoURL.match(/github\.com\/([^\/]+)/);
      if (match) {
        githubUsername = match[1];
      }
    }

    const authUser: AuthUser = {
      uid: user.uid,
      email: email,
      displayName: displayName,
      photoURL: user.photoURL,
      providerId: "github.com",
    };

    console.log("GitHub auth user data:", authUser);
    console.log("GitHub username:", githubUsername);

    // Send to backend for verification/registration
    await authenticateWithBackend(authUser, await user.getIdToken(), githubUsername);

    return authUser;
  } catch (error: any) {
    console.error("GitHub sign-in error:", error);

    // Handle account exists with different credential error
    if (error.code === "auth/account-exists-with-different-credential") {
      console.log(
        "Account exists with different credential, attempting to link accounts..."
      );

      // Get the email from the error
      const email = error.customData?.email;

      if (email) {
        // Show a user-friendly message and suggest using the original provider
        const errorMessage = `An account with email ${email} already exists. Please sign in with your original authentication method (Google) to access your account.`;
        console.error("Account linking error:", errorMessage);
        throw new Error(errorMessage);
      } else {
        throw new Error(
          "Account already exists with a different sign-in method. Please use your original sign-in method (Google) to access your account."
        );
      }
    }

    throw error;
  }
};

export const signInWithMicrosoft = async (): Promise<AuthUser | null> => {
  try {
    const result = await signInWithPopup(auth, microsoftProvider);
    const user = result.user;

    const authUser: AuthUser = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      providerId: "microsoft.com",
    };

    // Send to backend for verification/registration
    await authenticateWithBackend(authUser, await user.getIdToken());

    return authUser;
  } catch (error) {
    console.error("Microsoft sign-in error:", error);
    throw error;
  }
};

// Backend authentication function
const authenticateWithBackend = async (
  authUser: AuthUser,
  firebaseToken: string,
  githubUsername?: string | null
) => {
  try {
    // Use the correct endpoint - auth routes are mounted at /api/auth
    const url = `${import.meta.env.VITE_API_URL || "http://localhost:3000"}/api/auth/firebase`;
    console.log("Making authentication request to:", url);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firebaseToken,
        user: {
          uid: authUser.uid,
          email: authUser.email,
          displayName: authUser.displayName,
          photoURL: authUser.photoURL,
          providerId: authUser.providerId,
          githubUsername: githubUsername,
        },
      }),
    });

    console.log("Response status:", response.status);
    console.log("Response headers:", Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Response error:", errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    console.log("Response data:", data);

    if (data.success && data.user) {
      // Store Firebase token for API calls
      localStorage.setItem("firebaseToken", firebaseToken);
      localStorage.setItem("firebaseUser", JSON.stringify(authUser));
      
      // Update user context with backend user data
      updateUserContext(data.user);

      // Redirect to dashboard
      window.location.href = "/";
    } else {
      throw new Error("Authentication failed");
    }
  } catch (error: any) {
    console.error("Backend authentication error:", error);
    throw error;
  }
};

// Auth state listener
export const onAuthStateChange = (
  callback: (user: FirebaseUser | null) => void
) => {
  return onAuthStateChanged(auth, callback);
};

// Get current Firebase user
export const getCurrentFirebaseUser = (): FirebaseUser | null => {
  return auth.currentUser;
};

// Custom Authentication Functions for Testing
export const generateTestToken = async (email?: string, displayName?: string): Promise<string> => {
  try {
    const response = await api.post('/auth/generate-test-token', {
      email: email || 'test@quickdb.com',
      displayName: displayName || 'Test User'
    });

    if (response.data.success) {
      return response.data.token;
    } else {
      throw new Error(response.data.error || 'Failed to generate test token');
    }
  } catch (error) {
    console.error('Error generating test token:', error);
    throw error;
  }
};

export const signInWithCustomToken = async (token: string): Promise<AuthUser | null> => {
  try {
    const response = await api.post('/auth/custom', {
      customToken: token
    });

    if (response.data.success) {
      const user = response.data.user;
      
      // Store the custom token for API calls
      localStorage.setItem("customToken", token);
      
      // Update user context
      updateUserContext({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        picture: user.picture,
        tier: user.tier,
      });

      return {
        uid: user.id,
        email: user.email,
        displayName: user.firstName,
        photoURL: user.picture,
        providerId: "custom",
      };
    } else {
      throw new Error(response.data.error || 'Custom authentication failed');
    }
  } catch (error) {
    console.error('Custom sign-in error:', error);
    throw error;
  }
};

export const isDevelopmentEnvironment = (): boolean => {
  // Only use proper environment indicators - import.meta.env.DEV is set by Vite
  // This is the safe way to detect development environment
  return import.meta.env.DEV === true;
};
